import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Check, Truck, X, MessageCircle, Search,
  ChevronLeft, ChevronRight, ArrowUp, ArrowDown,
  MapPin, Phone, Package, AlertCircle, Tag, Clock,
} from 'lucide-react';
import {
  getDeliveryStatus,
  formatOrderCutoffCta,
  formatEstimatedDeliveryLabel,
  formatPickupText,
  formatCutoffNotice,
  formatCutoffTime,
  isOrderingExpired,
} from '../utils/deliveryHelpers';

import { getApiBaseUrl } from '@/lib/apiBase';

const API = getApiBaseUrl();
const WHATSAPP_PHONE = (process.env.REACT_APP_WHATSAPP_PHONE || '').replace(/\D/g, '');
const STORE_PHONE    = process.env.REACT_APP_STORE_PHONE || process.env.REACT_APP_WHATSAPP_PHONE || '';
const STORE_2GIS_URL = process.env.REACT_APP_STORE_2GIS_URL || '';
const STORE_NAME     = 'АвтоВАЗ.KZ — запчасти для авто';
const STORE_ADDRESS  = 'г. Петропавловск, ул. Интернациональная 21';

function formatPrice(price) {
  return Math.round(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

function calcDiscounted(price, discountPercent) {
  return Math.round(price * (1 - discountPercent / 100));
}

function buildWhatsAppUrl(product, discount) {
  const hasDiscount = discount > 0;
  const displayPrice = hasDiscount ? calcDiscounted(product.client_price, discount) : product.client_price;
  const lines = ['Здравствуйте! Хочу заказать:', product.name];
  if (product.brand)   lines.push(`Бренд: ${product.brand}`);
  if (product.articul) lines.push(`Артикул: ${product.articul}`);
  if (product.oem)     lines.push(`OEM: ${product.oem}`);
  lines.push(hasDiscount
    ? `Цена по ссылке (скидка ${discount}%): ${formatPrice(displayPrice)} ₸`
    : `Цена: ${formatPrice(product.client_price)} ₸`
  );
  lines.push('', 'Пожалуйста, выставьте счёт на оплату.');
  return `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(lines.join('\n'))}`;
}

// ── Expired notice (weekday after 18:00) ─────────────────────────────────────
function ExpiredNotice({ shareCode, q, now }) {
  const status = getDeliveryStatus(now);
  let heading = 'Предложение устарело';
  let body;
  let waMsg;

  if (status === 'saturday_after') {
    heading = 'Время приёма заявок сегодня закончилось';
    body = 'Заявки по этой цене принимались до 13:00. Напишите нам — уточним актуальную цену, наличие и срок в понедельник.';
    waMsg = 'Здравствуйте! Хочу уточнить актуальную подборку запчастей (время приёма заявок сегодня прошло).';
  } else if (status === 'sunday') {
    heading = 'Сегодня выходной';
    body = 'В воскресенье поставки со склада нет. Напишите нам — подтвердим цену, наличие и срок в понедельник.';
    waMsg = 'Здравствуйте! Хочу уточнить актуальную подборку запчастей (воскресенье).';
  } else {
    // weekday_after
    body = 'Заявки на поставку по этой подборке принимались до 18:00. После этого времени цены, наличие и скидка могли измениться.';
    waMsg = 'Здравствуйте! Смотрел подборку запчастей, предложение устарело после 18:00.';
  }

  const lines = [waMsg];
  if (q) lines.push(`Запрос был: «${q}»`);
  if (shareCode) lines.push(`Код подборки: ${shareCode}`);
  lines.push('Пожалуйста, пришлите актуальную подборку с ценами.');
  const waUrl = WHATSAPP_PHONE
    ? `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(lines.join('\n'))}`
    : null;

  return (
    <div className="my-6 bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
        <Clock className="w-5 h-5 text-amber-600" />
      </div>
      <div className="font-semibold text-gray-900 text-base mb-1">{heading}</div>
      <p className="text-sm text-gray-600 mb-1">{body}</p>
      {shareCode && (
        <p className="text-xs text-gray-400 mb-3 font-mono">
          Код подборки: <span className="font-semibold text-gray-600">{shareCode}</span>
        </p>
      )}
      <p className="text-sm text-gray-500 mb-4">
        Напишите нам — пришлём актуальную подборку.
      </p>
      {waUrl && (
        <a href={waUrl} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold rounded-xl transition-colors">
          <MessageCircle className="w-4 h-4" />
          Запросить актуальную подборку
        </a>
      )}
    </div>
  );
}

// ── Promo block ──────────────────────────────────────────────────────────────
function PromoBlock({ discount, deliveryDays, now }) {
  const ctaText = formatOrderCutoffCta(deliveryDays, now);
  const notice  = formatCutoffNotice(now);
  const status  = getDeliveryStatus(now);
  const isUnconfirmed = status === 'saturday_after' || status === 'sunday' || status === 'weekday_after';

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-5">
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center">
          <Tag className="w-5 h-5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-gray-900 text-base leading-snug">
            Ваша персональная скидка — <span className="text-amber-700">{discount}%</span>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="flex items-center gap-2 bg-amber-100 border border-amber-300 rounded-lg px-3 py-2 mb-3">
        <Clock className="w-4 h-4 text-amber-600 flex-shrink-0" />
        <span className="font-semibold text-amber-800 text-sm">{ctaText}</span>
      </div>

      {notice && (
        <p className="text-xs text-gray-600 mb-2">{notice}</p>
      )}

      {!isUnconfirmed && (
        <p className="text-xs text-gray-500">
          После оплаты мы подтвердим заказ, и товар можно будет забрать в расчётный срок.
        </p>
      )}

      <div className="mt-3 flex items-center gap-1.5 text-xs text-amber-700 border-t border-amber-200 pt-2">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
        Скидка закреплена за этой ссылкой
      </div>
    </div>
  );
}

// ── CTA strip (no-discount pages) ─────────────────────────────────────────────
function CtaStrip({ deliveryDays, now }) {
  const ctaText = formatOrderCutoffCta(deliveryDays, now);
  return (
    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 mb-4">
      <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
      <span className="text-sm font-medium text-amber-800">{ctaText}</span>
    </div>
  );
}

// ── Info block ───────────────────────────────────────────────────────────────
function InfoBlock({ discount, deliveryDays, now }) {
  const hasDiscount    = discount > 0;
  const status         = getDeliveryStatus(now);
  const isUnconfirmed  = status === 'saturday_after' || status === 'sunday';
  const deliveryLabel  = formatEstimatedDeliveryLabel(deliveryDays, now);
  const pickupText     = formatPickupText(deliveryDays, now);
  const notice         = formatCutoffNotice(now);
  const cutoffTime     = formatCutoffTime(now);

  const steps = [
    'Выберите нужную запчасть и нажмите «Заказать»',
    'Мы выставим счёт на оплату',
    isUnconfirmed
      ? 'Оформите заявку — мы подтвердим цену и срок в понедельник'
      : `Оплатите заказ до ${cutoffTime}`,
    'После оплаты мы подтвердим заказ',
    pickupText,
  ];

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
          <Package className="w-4 h-4 text-amber-600" />
        </div>
        <h2 className="font-semibold text-gray-900 text-base">Как получить эти запчасти</h2>
      </div>

      <p className="text-sm text-gray-700 mb-3 leading-relaxed">
        Запчасти на складе поставщика.{' '}
        {deliveryLabel ? (
          <>
            Срок поставки в магазин —{' '}
            <span className="font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">
              {deliveryLabel}
            </span>
          </>
        ) : (
          <span className="font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">
            Срок поставки уточним при подтверждении заказа
          </span>
        )}
      </p>

      {hasDiscount && (
        <p className="text-xs text-blue-700 bg-blue-50 rounded-lg px-3 py-2 mb-3">
          Скидка {discount}% применяется при оформлении заказа по этой ссылке
        </p>
      )}

      <div className="space-y-2.5 mb-3">
        {steps.map((step, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
              {i + 1}
            </span>
            <span className="text-sm text-gray-700">{step}</span>
          </div>
        ))}
      </div>

      {/* Deadline warning */}
      <div className="flex items-start gap-1.5 border-t border-amber-200 pt-3 text-xs text-gray-600">
        <Clock className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
        <span>{notice}</span>
      </div>
      <div className="flex items-start gap-1.5 mt-2 text-xs text-gray-500">
        <AlertCircle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
        <span>Доставка со склада каждый день, <strong className="text-gray-700">кроме воскресенья</strong></span>
      </div>
    </div>
  );
}

// ── Store block ──────────────────────────────────────────────────────────────
function StoreBlock({ onTrack }) {
  const [photoVisible, setPhotoVisible] = useState(true);
  const storeWaUrl = WHATSAPP_PHONE
    ? `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent('Здравствуйте!')}`
    : null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mt-8">
      {photoVisible && (
        <img
          src="/store-photo.jpg"
          alt="Магазин АвтоВАЗ.KZ"
          className="w-full object-contain bg-gray-900"
          onError={() => setPhotoVisible(false)}
        />
      )}
      <div className="p-4">
        <div className="flex items-start gap-2 mb-3">
          <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-gray-900">{STORE_NAME}</div>
            <div className="text-sm text-gray-600 mt-0.5">{STORE_ADDRESS}</div>
          </div>
        </div>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Где забрать заказ</h3>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          {STORE_2GIS_URL && (
            <a href={STORE_2GIS_URL} target="_blank" rel="noopener noreferrer"
              onClick={() => onTrack?.('map_click')}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-green-500 hover:bg-green-600 text-white text-sm font-medium transition-colors">
              <MapPin className="w-4 h-4" /> Открыть в 2GIS
            </a>
          )}
          {storeWaUrl && (
            <a href={storeWaUrl} target="_blank" rel="noopener noreferrer"
              onClick={() => onTrack?.('whatsapp_click')}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-green-500 hover:bg-green-600 text-white text-sm font-medium transition-colors">
              <MessageCircle className="w-4 h-4" /> Написать в WhatsApp
            </a>
          )}
          {STORE_PHONE && (
            <a href={`tel:${STORE_PHONE}`}
              onClick={() => onTrack?.('phone_click')}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors">
              <Phone className="w-4 h-4" /> Позвонить
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Filter badge ─────────────────────────────────────────────────────────────
function ActiveFilterBadge({ children }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
      {children}
    </span>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function SharePage() {
  const [searchParams] = useSearchParams();

  const modeIds      = searchParams.get('mode') === 'ids';
  const q           = searchParams.get('q') || '';
  const inStockOnly = searchParams.get('inStock') === '1';
  const newOnly     = searchParams.get('newOnly') === '1';
  const backOnStock = searchParams.get('backOnStock') === '1';
  const sortBy      = searchParams.get('sortBy') || 'name';
  const sortOrder   = searchParams.get('sortOrder') || 'asc';
  const suppliers   = searchParams.get('suppliers') || '';
  const brands      = searchParams.get('brands') || '';
  const category    = searchParams.get('category') || '';
  const discountRaw  = parseInt(searchParams.get('discount') || '0') || 0;
  const discount     = discountRaw >= 1 && discountRaw <= 50 ? discountRaw : 0;
  const deliveryRaw  = parseInt(searchParams.get('delivery') || '0') || 0;
  const deliveryDays = deliveryRaw >= 1 && deliveryRaw <= 60 ? deliveryRaw : 0;
  const expiresRaw   = parseInt(searchParams.get('expires') || '0') || 0;
  const shareCode    = searchParams.get('_ref') || '';
  const isPreview    = searchParams.get('preview') === '1';

  // Compute now once per render so all helpers are consistent.
  const now = new Date();

  const expiredByTimestamp = expiresRaw > 0 && now.getTime() > expiresRaw * 1000;
  const expired = !isPreview && (expiredByTimestamp || (!expiresRaw && isOrderingExpired(now)));

  const trackEvent = useCallback(async (eventType, productData = null) => {
    if (!shareCode || isPreview) return;
    try {
      await fetch(`${API}/public/share/${shareCode}/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_type: eventType, ...productData }),
      });
    } catch {}
  }, [shareCode, isPreview]);

  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const pageSize = 50;

  const hasFilter = modeIds ? !!shareCode : (q || inStockOnly || newOnly || backOnStock || suppliers || brands || category);

  const fetchProductsByIds = useCallback(async () => {
    if (!shareCode) { setProducts([]); setTotal(0); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/public/share/${shareCode}/products`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setProducts(data.products || []);
      setTotal(data.total || 0);
    } catch {
      setProducts([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [shareCode]);

  const fetchProducts = useCallback(async (pageNum) => {
    if (!hasFilter) { setProducts([]); setTotal(0); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pageNum, page_size: pageSize });
      if (q)           params.set('search', q);
      if (inStockOnly) params.set('in_stock_only', 'true');
      if (newOnly)     params.set('new_only', 'true');
      if (backOnStock) params.set('back_on_stock_only', 'true');
      params.set('sort_by', sortBy);
      params.set('sort_order', sortOrder);
      if (suppliers) params.set('supplier_ids', suppliers);
      if (brands)    params.set('brands', brands);
      if (category)  params.set('category', category);
      const res = await fetch(`${API}/products?${params}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setProducts(data.products || []);
      setTotal(data.total || 0);
    } catch {
      setProducts([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q, inStockOnly, newOnly, backOnStock, sortBy, sortOrder, suppliers, brands, category, hasFilter]);

  useEffect(() => {
    if (modeIds) { fetchProductsByIds(); }
    else { setPage(1); fetchProducts(1); }
  }, [modeIds, fetchProductsByIds, fetchProducts]);
  useEffect(() => { if (!modeIds && page > 1) fetchProducts(page); }, [modeIds, page, fetchProducts]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const sortLabel  = sortBy === 'client_price' ? 'Цена' : sortBy === 'in_stock' ? 'Остаток' : 'Название';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm select-none">А</div>
          <div>
            <div className="font-semibold text-gray-900 leading-tight">АвтоВАЗ.KZ</div>
            <div className="text-xs text-gray-500">Запчасти для авто</div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5">

        {/* Filter badges */}
        {hasFilter && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {!modeIds && q && (
              <div className="flex items-center gap-1.5 text-sm text-gray-700 font-medium mr-1">
                <Search className="w-4 h-4 text-gray-400" />{q}
              </div>
            )}
            {!modeIds && inStockOnly  && <ActiveFilterBadge>В наличии</ActiveFilterBadge>}
            {!modeIds && newOnly      && <ActiveFilterBadge>Новинки</ActiveFilterBadge>}
            {!modeIds && backOnStock  && <ActiveFilterBadge>Появились на складе</ActiveFilterBadge>}
            {!modeIds && brands && brands.split(',').map(b => <ActiveFilterBadge key={b}>{b.trim()}</ActiveFilterBadge>)}
            {!modeIds && (sortBy !== 'name' || sortOrder !== 'asc') && (
              <ActiveFilterBadge>
                {sortLabel}
                {sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
              </ActiveFilterBadge>
            )}
            {!loading && total > 0 && (
              <span className="ml-auto text-xs text-gray-400">
                {total} {total === 1 ? 'товар' : total >= 2 && total <= 4 ? 'товара' : 'товаров'}
              </span>
            )}
          </div>
        )}

        {/* Promo block (with discount) */}
        {hasFilter && discount > 0 && <PromoBlock discount={discount} deliveryDays={deliveryDays} now={now} />}

        {/* CTA strip (no discount) */}
        {hasFilter && discount === 0 && <CtaStrip deliveryDays={deliveryDays} now={now} />}

        {/* Info block */}
        {hasFilter && <InfoBlock discount={discount} deliveryDays={deliveryDays} now={now} />}

        {/* After-cutoff notice instead of products (weekday only) */}
        {hasFilter && expired && <ExpiredNotice shareCode={shareCode} q={q} now={now} />}

        {/* Empty state */}
        {!hasFilter && (
          <div className="text-center py-20 text-gray-500">
            <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Нет параметров поиска</p>
          </div>
        )}

        {/* Loader */}
        {!expired && loading && (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        )}

        {/* No results */}
        {!expired && !loading && hasFilter && products.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            <p className="text-lg font-medium">Ничего не найдено</p>
          </div>
        )}

        {/* Product cards */}
        {!expired && !loading && products.length > 0 && (
          <>
            <div className="space-y-3">
              {products.map((product) => {
                const originalPrice   = product.client_price;
                const discountedPrice = discount > 0 ? calcDiscounted(originalPrice, discount) : null;

                return (
                  <div key={product.id || product.code}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">

                    <div className="flex items-start gap-3">
                      {/* Stock indicator */}
                      <div className="flex-shrink-0 mt-0.5">
                        {product.in_stock > 0 ? (
                          <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
                            <Check className="w-4 h-4 text-green-600" />
                          </div>
                        ) : product.in_transit ? (
                          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
                            <Truck className="w-4 h-4 text-blue-600" />
                          </div>
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center">
                            <X className="w-4 h-4 text-red-500" />
                          </div>
                        )}
                      </div>

                      {/* Name + meta */}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 text-sm leading-snug">{product.name}</div>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                          {product.brand   && <span>Бренд: <span className="text-gray-700 font-medium">{product.brand}</span></span>}
                          {product.articul && <span>Арт.: <span className="text-gray-700">{product.articul}</span></span>}
                          {product.oem     && <span>OEM: <span className="text-gray-700">{product.oem}</span></span>}
                        </div>
                      </div>
                    </div>

                    {/* Price row */}
                    <div className="mt-3 flex items-end justify-between gap-3">
                      <div>
                        {discountedPrice !== null ? (
                          <>
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className="text-xl font-bold text-gray-900">
                                {formatPrice(discountedPrice)} ₸
                              </span>
                              <span className="text-sm text-gray-400 line-through">
                                {formatPrice(originalPrice)} ₸
                              </span>
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-red-100 text-red-600 text-xs font-bold">
                                -{discount}%
                              </span>
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">Цена по вашей ссылке</div>
                          </>
                        ) : (
                          <div className="text-xl font-bold text-gray-900">
                            {formatPrice(originalPrice)} ₸
                          </div>
                        )}
                        <div className="text-xs mt-0.5">
                          {product.in_stock > 0 ? (
                            <span className="text-green-600 font-medium">В наличии</span>
                          ) : product.in_transit ? (
                            <span className="text-blue-600 font-medium">В пути на склад</span>
                          ) : (
                            <span className="text-red-500">Нет в наличии</span>
                          )}
                        </div>
                      </div>

                      {WHATSAPP_PHONE && (
                        <a
                          href={buildWhatsAppUrl(product, discount)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackEvent('order_click', {
                            product_id: product.id || product.code || '',
                            product_name: product.name || '',
                            brand: product.brand || '',
                            articul: product.articul || product.oem || '',
                            original_price: product.client_price,
                            discount_percent: discount || null,
                            final_price: discount > 0 ? calcDiscounted(product.client_price, discount) : product.client_price,
                          })}
                          className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 bg-green-500 hover:bg-green-600 active:bg-green-700 text-white text-sm font-semibold rounded-xl transition-colors"
                        >
                          <MessageCircle className="w-4 h-4" />
                          Заказать
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {!modeIds && totalPages > 1 && (
              <div className="mt-5 flex items-center justify-center gap-3">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="p-2.5 rounded-xl border border-gray-200 disabled:opacity-40 hover:bg-gray-100 transition-colors">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-600">{page} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="p-2.5 rounded-xl border border-gray-200 disabled:opacity-40 hover:bg-gray-100 transition-colors">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </>
        )}

        {/* Store block */}
        {hasFilter && <StoreBlock onTrack={trackEvent} />}

      </main>

      <footer className="mt-10 pb-6 text-center text-xs text-gray-400">{STORE_NAME}</footer>
    </div>
  );
}
